import { useState } from 'react';
import { Flag } from '../ui/country-select.jsx';

/**
 * Recursive tree-view of OSS units with inline personnel cards.
 * Selection state lifted up via onSelect(unit | person, kind).
 */
export function UnitTree({
  roots,
  selection,
  onSelect,
  onAddSubUnit,
  onAddPerson,
  onEditUnit,
  onEditPerson,
  onDeleteUnit,
  onDeletePerson,
  onMoveUnit,
  onMovePerson,
  isAdmin,
  inheritedHome,    // map: unitId -> { id, name } inherited from ancestors
}) {
  if (roots.length === 0) {
    return (
      <div className="card flex flex-col items-center justify-center px-6 py-12 text-center">
        <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-accent-soft text-accent">
          <i className="fa-solid fa-sitemap text-lg" aria-hidden="true" />
        </div>
        <p className="text-sm font-medium text-ink">No units yet</p>
        <p className="mt-1 max-w-sm text-sm text-mist">
          Create the first organizational unit to start building the hierarchy.
        </p>
      </div>
    );
  }
  return (
    <ul className="card overflow-hidden">
      {roots.map((u) => (
        <UnitNode
          key={u.id}
          unit={u}
          depth={0}
          selection={selection}
          onSelect={onSelect}
          onAddSubUnit={onAddSubUnit}
          onAddPerson={onAddPerson}
          onEditUnit={onEditUnit}
          onEditPerson={onEditPerson}
          onDeleteUnit={onDeleteUnit}
          onDeletePerson={onDeletePerson}
          onMoveUnit={onMoveUnit}
          onMovePerson={onMovePerson}
          isAdmin={isAdmin}
          inheritedHome={inheritedHome}
        />
      ))}
    </ul>
  );
}

function UnitNode({ unit, depth, selection, onSelect, ...handlers }) {
  const [open, setOpen] = useState(true);
  const isSelectedUnit = selection?.kind === 'unit' && selection.id === unit.id;
  const childCount = unit.children.length;
  const personCount = unit.persons.length;
  const subtreeCount = countSubtreePersons(unit);

  const showHomeInherited = !unit.homeNodeId && handlers.inheritedHome?.[unit.id];

  return (
    <li className="border-b border-line last:border-b-0">
      <div
        className={
          'group flex items-center gap-2 px-3 py-2.5 transition-colors ' +
          (isSelectedUnit ? 'bg-accent-soft' : 'hover:bg-canvas')
        }
        style={{ paddingLeft: `${12 + depth * 18}px` }}
      >
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="flex h-6 w-6 shrink-0 items-center justify-center rounded text-mist hover:bg-line/60"
          aria-label={open ? 'Collapse' : 'Expand'}
        >
          {(childCount > 0 || personCount > 0) ? (
            <i className={`fa-solid fa-chevron-${open ? 'down' : 'right'} text-[10px]`} aria-hidden="true" />
          ) : (
            <i className="fa-solid fa-circle text-[3px]" aria-hidden="true" />
          )}
        </button>

        <button
          type="button"
          onClick={() => onSelect({ kind: 'unit', id: unit.id })}
          className="flex min-w-0 flex-1 items-center gap-2 text-left"
        >
          <div className={'flex h-7 w-7 shrink-0 items-center justify-center rounded-lg ' + (isSelectedUnit ? 'bg-white text-accent' : 'bg-accent-soft text-accent')}>
            <i className="fa-solid fa-sitemap text-[11px]" aria-hidden="true" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <span className={'truncate font-semibold ' + (isSelectedUnit ? 'text-accent' : 'text-ink')}>
                {unit.name}
              </span>
              {unit.shortName && (
                <span className="rounded border border-line bg-white px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wider text-mist">
                  {unit.shortName}
                </span>
              )}
              {unit.countryCode && <Flag code={unit.countryCode} size={12} />}
            </div>
            <div className="mt-0.5 flex items-center gap-2 text-[11px] text-mist">
              {childCount > 0 && (
                <span><i className="fa-solid fa-folder-tree mr-1 text-[9px]" aria-hidden="true" />{childCount} sub-unit{childCount === 1 ? '' : 's'}</span>
              )}
              {personCount > 0 && (
                <span><i className="fa-solid fa-user mr-1 text-[9px]" aria-hidden="true" />{personCount} direct</span>
              )}
              {subtreeCount > personCount && (
                <span className="text-mist/70">({subtreeCount} subtree)</span>
              )}
              {unit.homeNodeId && (
                <span className="inline-flex items-center gap-1 rounded border border-line bg-white px-1.5 py-0.5">
                  <i className="fa-solid fa-server text-[9px] text-accent" aria-hidden="true" />
                  home
                </span>
              )}
              {showHomeInherited && (
                <span className="inline-flex items-center gap-1 rounded border border-dashed border-line bg-canvas px-1.5 py-0.5 text-mist">
                  <i className="fa-solid fa-arrow-up text-[9px]" aria-hidden="true" />
                  inherits home
                </span>
              )}
            </div>
          </div>
        </button>

        {handlers.isAdmin && (
          <div className="flex items-center gap-0.5 opacity-0 transition-opacity group-hover:opacity-100">
            <RowAction icon="fa-folder-plus"  title="Add sub-unit"  onClick={() => handlers.onAddSubUnit(unit)} />
            <RowAction icon="fa-user-plus"    title="Add person"    onClick={() => handlers.onAddPerson(unit)} />
            <RowAction icon="fa-pen"          title="Edit unit"     onClick={() => handlers.onEditUnit(unit)} />
            <RowAction icon="fa-arrows-up-down-left-right" title="Move unit" onClick={() => handlers.onMoveUnit(unit)} />
            <RowAction icon="fa-trash"        title="Delete unit"   tone="rose" onClick={() => handlers.onDeleteUnit(unit)} />
          </div>
        )}
      </div>

      {open && (
        <>
          {personCount > 0 && (
            <ul>
              {unit.persons.map((p) => (
                <PersonRow
                  key={p.id}
                  person={p}
                  unit={unit}
                  depth={depth + 1}
                  selection={selection}
                  onSelect={onSelect}
                  onEdit={handlers.onEditPerson}
                  onDelete={handlers.onDeletePerson}
                  onMove={handlers.onMovePerson}
                  isAdmin={handlers.isAdmin}
                />
              ))}
            </ul>
          )}
          {childCount > 0 && (
            <ul>
              {unit.children.map((child) => (
                <UnitNode
                  key={child.id}
                  unit={child}
                  depth={depth + 1}
                  selection={selection}
                  onSelect={onSelect}
                  {...handlers}
                />
              ))}
            </ul>
          )}
        </>
      )}
    </li>
  );
}

function PersonRow({ person, unit, depth, selection, onSelect, onEdit, onDelete, onMove, isAdmin }) {
  const isSelected = selection?.kind === 'person' && selection.id === person.id;
  return (
    <li className="border-t border-line/60">
      <div
        className={
          'group flex items-center gap-2 px-3 py-2 transition-colors ' +
          (isSelected ? 'bg-accent-soft' : 'hover:bg-canvas')
        }
        style={{ paddingLeft: `${12 + depth * 18}px` }}
      >
        <span className="flex h-6 w-6 shrink-0 items-center justify-center text-[10px] text-mist">
          <i className="fa-solid fa-angle-right text-[9px]" aria-hidden="true" />
        </span>
        <button
          type="button"
          onClick={() => onSelect({ kind: 'person', id: person.id, unitId: unit.id })}
          className="flex min-w-0 flex-1 items-center gap-2 text-left"
        >
          <div className={'flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[10px] font-semibold ' + (person.hasDevice ? 'bg-accent-soft text-accent' : 'bg-canvas text-mist')}>
            {(person.displayName || '?').charAt(0).toUpperCase()}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              {person.rank && (
                <span className="font-mono text-[10px] uppercase text-mist">{person.rank}</span>
              )}
              <span className={'truncate text-[13px] ' + (isSelected ? 'font-semibold text-accent' : 'text-ink')}>
                {person.displayName}
              </span>
              {person.callsign && (
                <span className="rounded border border-line bg-white px-1.5 py-0.5 font-mono text-[10px] text-mist">
                  {person.callsign}
                </span>
              )}
              {person.hasDevice && (
                <span className="inline-flex items-center gap-1 rounded-full bg-accent-soft px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-accent">
                  <i className="fa-solid fa-mobile-screen text-[9px]" aria-hidden="true" />
                  device
                </span>
              )}
            </div>
            {person.position && (
              <div className="text-[11px] text-mist">{person.position}</div>
            )}
          </div>
        </button>
        {isAdmin && (
          <div className="flex items-center gap-0.5 opacity-0 transition-opacity group-hover:opacity-100">
            <RowAction icon="fa-pen"          title="Edit"   onClick={() => onEdit(person, unit)} />
            <RowAction icon="fa-arrows-up-down-left-right" title="Move" onClick={() => onMove(person)} />
            <RowAction icon="fa-trash"        title="Delete" tone="rose" onClick={() => onDelete(person)} />
          </div>
        )}
      </div>
    </li>
  );
}

function RowAction({ icon, title, onClick, tone = 'mist' }) {
  const tones = {
    mist: 'hover:bg-line/60 text-mist hover:text-ink',
    rose: 'hover:bg-rose-soft text-mist hover:text-rose-600',
  };
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onClick?.(e);
      }}
      title={title}
      aria-label={title}
      className={`inline-flex h-7 w-7 items-center justify-center rounded-lg transition-colors ${tones[tone]}`}
    >
      <i className={`fa-solid ${icon} text-[11px]`} aria-hidden="true" />
    </button>
  );
}

function countSubtreePersons(unit) {
  let n = unit.persons.length;
  for (const c of unit.children) n += countSubtreePersons(c);
  return n;
}
